// Imports remain unchanged

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  Button,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import dayjs from "dayjs";
import BasicModal from "../../components/BasicModal";
import { LARGE_FORM_MODAL_SX, STANDARD_FORM_HEADING_SX, FORM_CANCEL_BUTTON_SX, FORM_SUBMIT_BUTTON_SX } from "../../utils/formModalStyles";
import { useParams } from "react-router-dom";
import { getAssignmentByAdmissionId } from "../../services/PatientAssignmentService";
import { getTreatmentsByAdmissionId } from "../../services/PatientTreatmentService";
import { getByAdmissionId } from "../../services/PatientConsumablesService";
import { getVisitsByAdmission } from "../../services/DoctorVisitService";
import { getDischargesByAdmissionId } from "../../services/DischargeService";
import { getByAdmission } from "../../services/PatientOperationService";
import { getPatientById } from "../../services/patientService";
import { getById } from "../../services/PatientAdmissionService";
import { getLabTestsByAdmissionId } from "../../services/PatientlabTestService";
import PatientCheckoutModal from "../inPatientDetail/IpdCheckout.jsx";
import {
  buildIpdSummaryDocumentHtml,
  exportPatientSummaryDocumentToPdf,
  getPatientPdfFilename,
  printPatientSummaryDocument,
} from "../../utils/patientSummaryDocumentUtils";

const IPDInvoice = ({ open, admissionId, onClose }) => {
  const { patientId } = useParams();
  const [AssignmentList, setAssignmentList] = useState([]);
  const [treatmentList, setTreatmentList] = useState([]);
  const [patientInfo, setPatientInfo] = useState({});
  const [consumableList, setConsumableList] = useState([]);
  const [visitList, setVisitList] = useState([]);
  const [operationList, setOperationList] = useState([]);
  const [admissionData, setAdmissionData] = useState([]);
  const [dischargeList, setDischargeList] = useState([]);
  const [labTestList, setlabTestList] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [open_checkout_modal, setOpenCheckoutModal] = useState(false);

  useEffect(() => {
    if (admissionId) {
      fetchAssignments();
      fetchTreatments();
      fetchConsumables();
      fetchOperations();
      fetchAdmissionById();
      fetchAllDischarge();
      fetchPatientLabTest();
    }
    if (patientId) fetchPatientDetails();
  }, [admissionId, patientId]);

  const fetchPatientDetails = async () => {
    try {
      const data = await getPatientById(patientId);
      setPatientInfo(data);
    } catch (error) {
      console.error("Failed to fetch patient info", error);
    }
  };

  const fetchAssignments = async () => {
    try {
      const data = await getAssignmentByAdmissionId(admissionId);
      setAssignmentList(data);
    } catch (error) {
      console.error("Failed to fetch assignments", error);
    }
  };

  const fetchTreatments = async () => {
    try {
      const data = await getTreatmentsByAdmissionId(admissionId);
      setTreatmentList(data);
    } catch (error) {
      console.error("Failed to fetch treatments", error);
    }
  };

  const fetchOperations = async () => {
    try {
      const data = await getByAdmission(admissionId);
      setOperationList(data);
    } catch (error) {
      console.error("Failed to fetch operations", error);
    }
  };

  const fetchConsumables = async () => {
    try {
      const data = await getByAdmissionId(admissionId);
      setConsumableList(data);
    } catch (error) {
      console.error("Failed to fetch consumables", error);
    }
  };

  const fetchVisists = async () => {
    try {
      const data = await getVisitsByAdmission(admissionId);
      setVisitList(data);
    } catch (error) {
      console.error("Failed to fetch consumables", error);
    }
  };

  const fetchAdmissionById = async () => {
    try {
      const data = await getById(admissionId);
      setAdmissionData(data);
    } catch (error) {
      console.error("Failed to fetch admission", error);
    }
  };

  const fetchAllDischarge = async () => {
    try {
      const data = await getDischargesByAdmissionId(admissionId);
      setDischargeList(data);
    } catch (error) {
      console.error("Failed to fetch discharge", error);
    }
  };

  const fetchPatientLabTest = async () => {
    try {
      const data = await getLabTestsByAdmissionId(admissionId);
      setlabTestList(data);
    } catch (error) {
      console.error("Failed to fetch lab test", error);
    }
  };

  const formatDate = (dateArr) => {
    if (!Array.isArray(dateArr)) return "Invalid Date";
    const d = new Date(...dateArr);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const buildIpdInvoiceHtml = () =>
    buildIpdSummaryDocumentHtml({
      hospitalName: "Hospital",
      patient: patientInfo,
      admission: admissionData,
      assignment: AssignmentList,
      treatments: treatmentList,
      operations: operationList,
      labTests: labTestList,
      consumables: consumableList,
      visits: visitList,
      dischargeList,
      prescriptions: [],
      formatDateFn: formatDate,
    });

  const exportToPDF = async () => {
    await exportPatientSummaryDocumentToPdf({
      bodyHtml: buildIpdInvoiceHtml(),
      filename: getPatientPdfFilename("ipd_invoice", patientId),
    });
  };

  const handlePrint = () => {
    printPatientSummaryDocument({
      title: "IPD Invoice",
      bodyHtml: buildIpdInvoiceHtml(),
    });
  };

  return (
    <BasicModal open={open} onClose={onClose} sx={LARGE_FORM_MODAL_SX}>
      <Paper
        elevation={3}
        sx={{
          backgroundColor: "#fff",
          borderRadius: 2,
          p: 1.25,
          overflow: "visible",
        }}
      >
        <div>
          <Typography sx={STANDARD_FORM_HEADING_SX}>
            IPD Invoice
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Typography fontSize={14}><strong>Patient Name:</strong> {patientInfo.name}</Typography>
            <Typography fontSize={14}><strong>Contact:</strong> {patientInfo.mobile}</Typography>
            <Typography fontSize={14}><strong>Address:</strong> {patientInfo.address}</Typography>
            <Typography fontSize={14}>
              <strong>Registration Date:</strong>{" "}
              {patientInfo.registrationDate
                ? dayjs(patientInfo.registrationDate).format("DD/MM/YYYY hh:mm A")
                : ""}
            </Typography>
            {/* <Typography fontSize={14}><strong>Discharge Date:</strong> - </Typography> */}
            <Typography fontSize={14}><strong>Advance Paid:</strong> ₹{admissionData?.admissionAmount}</Typography>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* ======================= SECTION TOTALS ======================= */}
          {/** Compute totals before return if this code is in a component */}
          {(() => {
            // Compute total values
            // const totalBedPrice = AssignmentList.reduce((sum, item) => sum + parseFloat(item.bedPrice || 0), 0);
            const totalBedPrice = AssignmentList?.bedPrice ? parseFloat(AssignmentList.bedPrice) : 0;
            const totalTreatmentPrice = treatmentList.reduce((sum, item) => sum + parseFloat(item.medicinePrice || 0), 0);
            const totalLabTestPrice = labTestList
              .filter(item => item.testStatus === "COMPLETED") // ✅ Only include completed tests
              .reduce((sum, item) => sum + parseFloat(item.testPrice || 0), 0);

            const totalOperationPrice = operationList.reduce((sum, item) => sum + parseFloat(item.operationPrice || 0), 0);
            const totalConsumablePrice = consumableList.reduce(
              (sum, item) => sum + (parseFloat(item.unitPrice || 0) * parseInt(item.quantity || 1)),
              0
            );
            const advanceAmount = parseFloat(admissionData?.admissionAmount || 0);
            const grandTotal = totalBedPrice + totalTreatmentPrice + totalOperationPrice + totalConsumablePrice + totalLabTestPrice;
            const netPayable = grandTotal - advanceAmount;

            // Assign to local scope for rendering below
            return (
              <>
                {/* ✅ Bed & Ward Assignment Table */}
                {/* <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Bed & Ward Assignment Details
                  </Typography>
                  <Table size="small">
                    <TableHead sx={{ backgroundColor: "#568AF7" }}>
                      <TableRow>
                        <TableCell>S.No.</TableCell>
                        <TableCell>Admission Date</TableCell>
                        <TableCell>Bed</TableCell>
                        <TableCell>Ward</TableCell>
                        <TableCell>Price</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {AssignmentList.map((item, i) => (
                        <TableRow key={item.id || i}>
                          <TableCell>{i + 1}</TableCell>
                          <TableCell>{formatDate(item.admissionDate)}</TableCell>
                          <TableCell>{item.bedName || "-"}</TableCell>
                          <TableCell>{item.wardName || "-"}</TableCell>
                          <TableCell>
                            {item.bedPrice != null ? parseFloat(item.bedPrice).toFixed(2) : "0.00"}
                          </TableCell>

                        </TableRow>
                      ))}
                    </TableBody>
                  </Table> 
                </Box> */}

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Bed & Ward Assignment Details
                  </Typography>
                  <Table size="small">
                    <TableHead sx={{ backgroundColor: "#568AF7" }}>
                      <TableRow>
                        <TableCell>S.No.</TableCell>
                        <TableCell>Admission Date</TableCell>
                        <TableCell>Bed</TableCell>
                        <TableCell>Ward</TableCell>
                        <TableCell>Price</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {AssignmentList ? (
                        <TableRow>
                          <TableCell>1</TableCell>
                          <TableCell>{formatDate(AssignmentList.admissionDate)}</TableCell>
                          <TableCell>{AssignmentList.bedName || "-"}</TableCell>
                          <TableCell>{AssignmentList.wardName || "-"}</TableCell>
                          <TableCell>
                            {AssignmentList.bedPrice != null
                              ? parseFloat(AssignmentList.bedPrice).toFixed(2)
                              : "0.00"}
                          </TableCell>
                        </TableRow>
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            No assignment found for this admission.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
                
                {/* ✅ Treatments */}
                {treatmentList.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Treatments & Medicine
                    </Typography>
                    <Table size="small">
                      <TableHead sx={{ backgroundColor: "#568AF7" }}>
                        <TableRow>
                          <TableCell>S.No.</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Time</TableCell>
                          <TableCell>Treatment Type</TableCell>
                          <TableCell>Medicine</TableCell>
                          <TableCell>Dosage</TableCell>
                          <TableCell>Performed By</TableCell>
                          <TableCell>Remarks</TableCell>
                          <TableCell>Price</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {treatmentList.map((item, index) => (
                          <TableRow key={item.id}>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>{formatDate(item.treatmentDate)}</TableCell>
                            <TableCell>
                              {item.treatmentTime?.[0]?.toString().padStart(2, "0")}:
                              {item.treatmentTime?.[1]?.toString().padStart(2, "0")}
                            </TableCell>
                            <TableCell>{item.treatmentType}</TableCell>
                            <TableCell>{item.medicine}</TableCell>
                            <TableCell>{item.dosage}</TableCell>
                            <TableCell>{item.performedByName}</TableCell>
                            <TableCell>{item.remarks}</TableCell>
                            <TableCell>
                              {item.medicinePrice != null ? parseFloat(item.medicinePrice).toFixed(2) : "0.00"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}

                {/* ✅ Lab tests */}
                {labTestList.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Lab Tests & Reports
                    </Typography>
                    <Table size="small">
                      <TableHead sx={{ backgroundColor: "#568AF7" }}>
                        <TableRow>
                          <TableCell>S.No.</TableCell>
                          <TableCell>Test Name</TableCell>
                          <TableCell>Assigned By</TableCell>
                          <TableCell>Path Name</TableCell>
                          <TableCell>Test Status</TableCell>
                          <TableCell>Price</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {labTestList
                          .filter((item) => item.testStatus === "COMPLETED")
                          .map((item, index) => (
                            <TableRow key={item.id}>
                              <TableCell>{index + 1}</TableCell>
                              <TableCell>{item.testName}</TableCell>
                              <TableCell>{item.assinedByName}</TableCell>
                              <TableCell>
                                {item.labName ? `Path: ${item.labName.charAt(0).toUpperCase()}${item.labName.slice(1)}` : ''}
                              </TableCell>
                              <TableCell>{item.testStatus}</TableCell>
                              <TableCell>{item.testPrice}.00</TableCell>
                            </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}

                {/* ✅ Operations */}
                {operationList.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Operation & Surgeries
                    </Typography>
                    <Table size="small">
                      <TableHead sx={{ backgroundColor: "#568AF7" }}>
                        <TableRow>
                          <TableCell>S.No.</TableCell>
                          <TableCell>Operation Name</TableCell>
                          <TableCell>Operation Date</TableCell>
                          <TableCell>Operation Time</TableCell>
                          <TableCell>Price</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {operationList.map((item, index) => (
                          <TableRow key={item.id}>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>{item.operationName}</TableCell>
                            <TableCell>{item.operationDate ? dayjs(item.operationDate).format("DD MMM YYYY") : "-"}</TableCell>
                            <TableCell>
                              {Array.isArray(item.operationTime)
                                ? dayjs().hour(item.operationTime[0]).minute(item.operationTime[1]).format("hh:mm A")
                                : "-"}
                            </TableCell>
                            <TableCell>
                              {item.operationPrice != null ? parseFloat(item.operationPrice).toFixed(2) : "0.00"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}

                {/* Consumables */}
                {consumableList.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Consumables Used
                    </Typography>
                    <Table size="small">
                      <TableHead sx={{ backgroundColor: "#568AF7" }}>
                        <TableRow>
                          <TableCell>S.No.</TableCell>
                          <TableCell>Date Used</TableCell>
                          <TableCell>Item</TableCell>
                          <TableCell>Quantity</TableCell>
                          <TableCell>Used By</TableCell>
                          <TableCell>Unit Price</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {consumableList.map((item, index) => (
                          <TableRow key={item.id}>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>{formatDate(item.dateUsed)}</TableCell>
                            <TableCell>{item.resourceName}</TableCell>
                            <TableCell>{item.quantity}</TableCell>
                            <TableCell>{item.usedByName}</TableCell>
                            <TableCell>{parseFloat(item.unitPrice || 0).toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}

                {/* Doctor Visits */}
                {visitList.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Doctor
                    </Typography>
                    <Table size="small">
                      <TableHead sx={{ backgroundColor: "#568AF7" }}>
                        <TableRow>
                          <TableCell>S.No.</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Visited By</TableCell>
                          <TableCell>Notes</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {visitList
                          .filter((v) => v.visitType === "doctor")
                          .map((item, index) => (
                            <TableRow key={item.id}>
                              <TableCell>{index + 1}</TableCell>
                              <TableCell>{formatDate(item.visitDate)}</TableCell>
                              <TableCell>{item.visitedByName}</TableCell>
                              <TableCell>{item.notes}</TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}

                {/* Discharge Formalities */}
                {dischargeList.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Discharge Formalities
                    </Typography>
                    <Table size="small">
                      <TableHead sx={{ backgroundColor: "#568AF7" }}>
                        <TableRow>
                          <TableCell sx={{ color: "#fff" }}>Discharge Date</TableCell>
                          <TableCell sx={{ color: "#fff" }}>Discharge Time</TableCell>
                          <TableCell sx={{ color: "#fff" }}>Discharge Type</TableCell>
                            <TableCell sx={{ color: "#fff" }}>Final Diagnose</TableCell>
                          <TableCell sx={{ color: "#fff" }}>Follow up Advice</TableCell>
                          <TableCell sx={{ color: "#fff" }}>Consition On Discharge</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dischargeList.map((item, index) => (
                          <TableRow key={item.id}>
                            <TableCell>
                              {Array.isArray(item.dischargeDate) && item.dischargeDate.length > 0
                                 ? dayjs(new Date(...item.dischargeDate)).format("DD MMM YYYY")
                                : item.dischargeDate
                                   ? dayjs(item.dischargeDate).format("DD MMM YYYY")
                                   : "-"
                              }
                              </TableCell>
                              <TableCell>
                                {dayjs()
                                  .hour(item.dischargeTime[0])
                                  .minute(item.dischargeTime[1])
                                  .format("hh:mm A")}
                              </TableCell>
                              <TableCell>{item.dischargeType || item.dischargeType}</TableCell>
                              <TableCell>{item.finalDiagnose || item.finalDiagnose}</TableCell>
                              <TableCell>{item.followUpAdvice || item.followUpAdvice}</TableCell>
                              <TableCell>{item.conditionOnDischarge || item.conditionOnDischarge}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}

                {/* =================== Summary Section =================== */}
                <Divider sx={{ my: 2 }} />

                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Summary of Charges
                    </Typography>

                    <Box sx={{ width: '100%' }}>
                      <Box display="flex" justifyContent="space-between" mb={0.5} width="100%">
                        <Typography variant="body2" fontWeight="bold" sx={{ textAlign: 'left' }}>
                          Total Bed Charges:
                        </Typography>
                        <Typography variant="body2" sx={{ textAlign: 'right', minWidth: 80 }}>
                          ₹{totalBedPrice.toFixed(2)}
                        </Typography>
                      </Box>

                      <Box display="flex" justifyContent="space-between" mb={0.5} width="100%">
                        <Typography variant="body2" fontWeight="bold" sx={{ textAlign: 'left' }}>
                          Total Treatment Charges:
                        </Typography>
                        <Typography variant="body2" sx={{ textAlign: 'right', minWidth: 80 }}>
                          ₹{totalTreatmentPrice.toFixed(2)}
                        </Typography>
                      </Box>

                      <Box display="flex" justifyContent="space-between" mb={0.5} width="100%">
                        <Typography variant="body2" fontWeight="bold" sx={{ textAlign: 'left' }}>
                          Total Lab Test Charges:
                        </Typography>
                        <Typography variant="body2" sx={{ textAlign: 'right', minWidth: 80 }}>
                          ₹{totalLabTestPrice.toFixed(2)}
                        </Typography>
                      </Box>

                      <Box display="flex" justifyContent="space-between" mb={0.5} width="100%">
                        <Typography variant="body2" fontWeight="bold" sx={{ textAlign: 'left' }}>
                          Total Operation Charges:
                        </Typography>
                        <Typography variant="body2" sx={{ textAlign: 'right', minWidth: 80 }}>
                          ₹{totalOperationPrice.toFixed(2)}
                        </Typography>
                      </Box>

                      <Box display="flex" justifyContent="space-between" mb={0.5} width="100%">
                        <Typography variant="body2" fontWeight="bold" sx={{ textAlign: 'left' }}>
                          Total Consumables:
                        </Typography>
                        <Typography variant="body2" sx={{ textAlign: 'right', minWidth: 80 }}>
                          ₹{totalConsumablePrice.toFixed(2)}
                        </Typography>
                      </Box>

                      <Divider sx={{ my: 1 }} />

                      <Box display="flex" justifyContent="space-between" mb={0.5} width="100%">
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ textAlign: 'left' }}>
                          Grand Total:
                        </Typography>
                        <Typography variant="subtitle2" sx={{ textAlign: 'right', minWidth: 80 }}>
                          ₹{grandTotal.toFixed(2)}
                        </Typography>
                      </Box>

                      <Box display="flex" justifyContent="space-between" mb={0.5} width="100%">
                        <Typography variant="subtitle2" fontWeight="bold" color="green" sx={{ textAlign: 'left' }}>
                          Admission Amount Paid:
                        </Typography>
                        <Typography variant="subtitle2" color="green" sx={{ textAlign: 'right', minWidth: 80 }}>
                          ₹{advanceAmount.toFixed(2)}
                        </Typography>
                      </Box>

                      <Box display="flex" justifyContent="space-between" mt={1} width="100%">
                        <Typography variant="h6" fontWeight="bold" color="red" sx={{ textAlign: 'left' }}>
                          Net Payable:
                        </Typography>
                        <Typography variant="h6" color="red" sx={{ textAlign: 'right', minWidth: 80 }}>
                          ₹{netPayable.toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
              </>
            );
          })()}
        </div>
        {/* Footer section  */}
        <Box
          display="flex"
          flexWrap="wrap"
          justifyContent="center"
          alignItems="center"
          mt={3}
          gap={2}
        >
          <Button variant="outlined" color="primary" onClick={onClose} sx={FORM_CANCEL_BUTTON_SX}>
            Cancel
          </Button>
          <Button variant="outlined" color="primary" onClick={exportToPDF} sx={FORM_CANCEL_BUTTON_SX}>
            PDF
          </Button>
          <Button variant="outlined" color="primary" onClick={handlePrint} sx={FORM_CANCEL_BUTTON_SX}>
            Print
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenCheckoutModal(true)}
            sx={FORM_SUBMIT_BUTTON_SX}
          >
            Pay Now
          </Button>
      </Box>
      </Paper>
      <PatientCheckoutModal
        open={open_checkout_modal}
        onClose={async () => {
          setOpenCheckoutModal(false);
        }}
      />
    </BasicModal>    
  );
};

export default IPDInvoice;